<template>
  <div class="monthly-closures">
    <section class="monthly-closures__header">
      <BasicTitle title="Cierres mensuales" />
      <BasicSubtitle subtitle="Consulta borradores y cierres definitivos ya generados." />
    </section>

    <section class="monthly-closures__filters">
      <div class="monthly-closures__field">
        <label for="closure-year">Anio</label>
        <InputText
          id="closure-year"
          v-model="filters.year"
          type="number"
          class="monthly-closures__control"
        />
      </div>
      <div class="monthly-closures__field">
        <label for="closure-month">Mes</label>
        <Dropdown
          id="closure-month"
          v-model="filters.month"
          :options="monthOptions"
          optionLabel="name"
          optionValue="id"
          placeholder="Todos"
          class="monthly-closures__control"
          :showClear="true"
        />
      </div>
      <div class="monthly-closures__field">
        <label for="closure-status">Estado</label>
        <Dropdown
          id="closure-status"
          v-model="filters.status"
          :options="statusOptions"
          optionLabel="name"
          optionValue="id"
          class="monthly-closures__control"
        />
      </div>
      <Button
        label="Buscar"
        class="monthly-closures__button"
        :disabled="isLoading"
        @click="loadClosures"
      />
    </section>

    <div v-if="isLoading" class="monthly-closures__loading">
      <ProgressSpinner />
      <p>Cargando cierres mensuales...</p>
    </div>

    <div v-if="errorMessage" class="monthly-closures__message monthly-closures__message--error">
      {{ errorMessage }}
    </div>

    <section class="monthly-closures__listing">
      <MobileTable
        v-if="tableRows.length"
        v-model:selection="selectedClosures"
        label="Cierres mensuales"
        :columns="tableColumns"
        :data="tableRows"
        :rows="20"
        :linked="true"
        :on-select="openDetail"
      />
      <DesktopTable
        v-if="tableRows.length"
        v-model:selection="selectedClosures"
        label="Cierres mensuales"
        :columns="tableColumns"
        :data="tableRows"
        :rows="20"
        :linked="true"
        :on-select="openDetail"
      />
      <p v-if="!tableRows.length && !isLoading" class="monthly-closures__empty">
        No hay cierres mensuales para mostrar.
      </p>
      <Actions v-if="tableRows.length" :items="listingActions" />
    </section>

    <section v-if="detailClosure" class="monthly-closures__detail">
      <div class="monthly-closures__detail-header">
        <div>
          <h3>Detalle del cierre {{ periodLabel(detailClosure) }}</h3>
          <p>
            {{ statusLabel(detailClosure.status) }}
            <span v-if="detailClosure.closedAt"> - Cerrado el {{ dateText(detailClosure.closedAt) }}</span>
          </p>
        </div>
        <button type="button" @click="clearDetail">Cerrar</button>
      </div>

      <div class="monthly-closures__cards">
        <article v-for="item in detailTotals" :key="item.label" class="monthly-closures__card">
          <span>{{ item.label }}</span>
          <strong>{{ item.value }}</strong>
        </article>
      </div>

      <div
        v-if="detailWarnings.length"
        class="monthly-closures__message monthly-closures__message--warn"
      >
        <strong>Warnings: {{ detailWarnings.length }}</strong>
        <ul>
          <li v-for="warning in detailWarnings" :key="warning">{{ warning }}</li>
        </ul>
      </div>

      <section class="monthly-closures__detail-section">
        <h4>Documentos</h4>
        <div v-if="detailDocuments.length" class="monthly-closures__documents">
          <a
            v-for="document in detailDocuments"
            :key="document.id"
            :href="document.fileUrl"
            target="_blank"
            rel="noopener noreferrer"
          >
            {{ documentLabel(document) }}
          </a>
        </div>
        <p v-else class="monthly-closures__empty monthly-closures__empty--left">
          No hay documentos generados.
        </p>
      </section>

      <section class="monthly-closures__detail-section">
        <h4>Liquidaciones por apartamento</h4>
        <div v-if="detailApartments.length" class="monthly-closures__table-wrap">
          <table class="monthly-closures__table monthly-closures__table--detail">
            <thead>
              <tr>
                <th>Apartamento</th>
                <th>Propietario</th>
                <th>Residente</th>
                <th>Gasto comun</th>
                <th>Fondo reserva</th>
                <th>Multas</th>
                <th>Pagos</th>
                <th>Total a pagar</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="apartment in detailApartments" :key="apartment.id">
                <td>{{ apartment.apartment }}</td>
                <td>{{ apartment.owner }}</td>
                <td>{{ apartment.resident }}</td>
                <td>{{ apartment.commonExpenseAmount }}</td>
                <td>{{ apartment.reserveFundAmount }}</td>
                <td>{{ apartment.finesAmount }}</td>
                <td>{{ apartment.paymentsAmount }}</td>
                <td>{{ apartment.totalToPay }}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p v-else class="monthly-closures__empty monthly-closures__empty--left">
          No hay liquidaciones por apartamento.
        </p>
      </section>
    </section>
  </div>
</template>

<script>
import { computed, onMounted, reactive, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import store from "../../store";
import Actions from "../../components/shared/Actions.vue";
import MobileTable from "../../widgets/tables/MobileTable.vue";
import DesktopTable from "../../widgets/tables/DesktopTable.vue";
import BasicTitle from "../../widgets/title/BasicTitle.vue";
import BasicSubtitle from "../../widgets/subtitle/BasicSubtitle.vue";
import { actions, currency, dates } from "../../constants";

const monthOptions = [
  { id: 1, name: "Enero" },
  { id: 2, name: "Febrero" },
  { id: 3, name: "Marzo" },
  { id: 4, name: "Abril" },
  { id: 5, name: "Mayo" },
  { id: 6, name: "Junio" },
  { id: 7, name: "Julio" },
  { id: 8, name: "Agosto" },
  { id: 9, name: "Septiembre" },
  { id: 10, name: "Octubre" },
  { id: 11, name: "Noviembre" },
  { id: 12, name: "Diciembre" },
];

const statusOptions = [
  { id: "all", name: "Todos" },
  { id: "draft", name: "Borrador" },
  { id: "closed", name: "Cerrado" },
];

const getValue = (source, paths, fallback = null) => {
  const list = Array.isArray(paths) ? paths : [paths];
  for (const path of list) {
    const value = String(path)
      .split(".")
      .reduce((acc, key) => (acc && acc[key] !== undefined ? acc[key] : undefined), source);
    if (value !== undefined && value !== null && value !== "") return value;
  }
  return fallback;
};

const text = (value) => {
  if (value === undefined || value === null || value === "") return "-";
  return String(value);
};

const number = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const money = (value) => currency.convertNumberToCurrency(number(value));

const dateText = (value) => (value ? dates.dateToString(value) : "-");

const normalizeList = (response) => {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.data)) return response.data;
  if (Array.isArray(response?.closures)) return response.closures;
  if (Array.isArray(response?.data?.closures)) return response.data.closures;
  return [];
};

const normalizeDocuments = (source) => {
  if (!source) return [];
  if (Array.isArray(source)) return source;
  const documents = source.documents !== undefined ? source.documents : source;
  if (Array.isArray(documents)) return documents;
  if (!documents || typeof documents !== "object") return [];

  const result = [];
  if (documents.buildingSummary) {
    result.push({ ...documents.buildingSummary, documentRole: "building_summary" });
  }
  if (Array.isArray(documents.apartmentLiquidations)) {
    documents.apartmentLiquidations.forEach((item) =>
      result.push({ ...item, documentRole: "apartment_liquidation" })
    );
  }
  return result;
};

export default {
  components: { Actions, BasicTitle, BasicSubtitle, DesktopTable, MobileTable },
  setup() {
    const route = useRoute();
    const router = useRouter();
    const now = new Date();
    const filters = reactive({
      year: now.getFullYear(),
      month: null,
      status: "all",
    });
    const selectedClosureId = ref(null);
    const selectedClosures = ref([]);

    const selectedBuilding = computed(() => store.state.general.selectedBuilding);
    const buildingId = computed(() => {
      const value = selectedBuilding.value;
      if (value && typeof value === "object") return value.id;
      return value || null;
    });
    const isLoading = computed(() => store.state.monthlyClosure.loading);
    const errorMessage = computed(() => store.state.monthlyClosure.error);
    const closures = computed(() => store.state.monthlyClosure.closures || []);
    const detailClosure = computed(() => store.state.monthlyClosure.currentClosure);

    const fetchParams = computed(() => {
      const params = {};
      if (buildingId.value) params.buildingId = buildingId.value;
      if (filters.year) params.year = Number(filters.year);
      if (filters.month) params.month = Number(filters.month);
      if (filters.status && filters.status !== "all") params.status = filters.status;
      return params;
    });

    const filteredClosures = computed(() =>
      closures.value.filter((closure) => {
        if (filters.year && Number(closure.year) !== Number(filters.year)) return false;
        if (filters.month && Number(closure.month) !== Number(filters.month)) return false;
        if (
          filters.status &&
          filters.status !== "all" &&
          String(closure.status).toLowerCase() !== filters.status
        ) {
          return false;
        }
        return true;
      })
    );
    const tableColumns = [
      { field: "period", header: "Mes/Anio", sort: true },
      { field: "building", header: "Edificio", sort: true },
      { field: "statusText", header: "Estado", sort: true },
      { field: "totalIncome", header: "Total ingresos", currency: true, sort: true },
      { field: "totalExpenses", header: "Total egresos", currency: true, sort: true },
      { field: "totalFines", header: "Total multas", currency: true, sort: true },
      { field: "totalCommonExpenses", header: "Gasto comun", currency: true, sort: true },
      { field: "totalReserveFund", header: "Fondo reserva", currency: true, sort: true },
      { field: "totalToPay", header: "Total a cobrar", currency: true, sort: true },
      { field: "createdAt", header: "Creado", date: true, sort: true },
      { field: "closedAt", header: "Cerrado", date: true, sort: true },
    ];
    const tableRows = computed(() =>
      filteredClosures.value.map((closure) => ({
        ...closure,
        period: periodLabel(closure),
        building: text(getValue(closure, ["building.name", "buildingName"])),
        statusText: statusLabel(closure.status),
        totalIncome: number(getTotal(closure, "totalIncome")),
        totalExpenses: number(getTotal(closure, "totalExpenses")),
        totalFines: number(getTotal(closure, "totalFines")),
        totalCommonExpenses: number(getTotal(closure, "totalCommonExpenses")),
        totalReserveFund: number(getTotal(closure, "totalReserveFund")),
        totalToPay: number(getTotal(closure, "totalToPay", "finalBalance")),
      }))
    );

    const detailWarnings = computed(() => detailClosure.value?.warnings || []);
    const detailDocuments = computed(() =>
      normalizeDocuments(detailClosure.value?.documents).map((document, index) => ({
        ...document,
        id: getValue(document, "id", index),
        fileUrl: getValue(document, ["fileUrl", "url", "path"], ""),
        documentRole: getValue(document, ["documentRole", "metadata.documentRole"], "pdf"),
      }))
    );
    const detailApartments = computed(() => {
      const source =
        detailClosure.value?.details ||
        detailClosure.value?.apartments ||
        detailClosure.value?.data?.apartments ||
        [];
      return source.map((item, index) => {
        const metadata = item.metadata || {};
        return {
          id: getValue(item, ["id", "apartmentId"], index),
          apartment: text(
            getValue(item, ["apartmentNumber", "apartment.number"], metadata.apartmentNumber)
          ),
          owner: text(getValue(item, ["ownerName", "owner.name"], metadata.owner)),
          resident: text(getValue(item, ["residentName", "resident.name"], metadata.resident)),
          commonExpenseAmount: money(getValue(item, "commonExpenseAmount", 0)),
          reserveFundAmount: money(getValue(item, "reserveFundAmount", 0)),
          finesAmount: money(getValue(item, ["fineAmount", "finesAmount"], 0)),
          paymentsAmount: money(getValue(item, "paymentsAmount", 0)),
          totalToPay: money(getValue(item, "totalToPay", 0)),
        };
      });
    });

    const detailTotals = computed(() => [
      { label: "Total ingresos", value: money(getTotal(detailClosure.value, "totalIncome")) },
      { label: "Total egresos", value: money(getTotal(detailClosure.value, "totalExpenses")) },
      { label: "Total multas", value: money(getTotal(detailClosure.value, "totalFines")) },
      {
        label: "Gasto comun",
        value: money(getTotal(detailClosure.value, "totalCommonExpenses")),
      },
      {
        label: "Fondo reserva",
        value: money(getTotal(detailClosure.value, "totalReserveFund")),
      },
      {
        label: "Total a cobrar",
        value: money(getTotal(detailClosure.value, "totalToPay", "finalBalance")),
      },
    ]);

    const ensureBreadcrumb = () => {
      const exists = store.state.general.breadcrumbs.find((item) => item.to === route.path);
      if (!exists) {
        store.commit("addBreadcrumb", { label: route.name, to: route.path });
      }
    };

    const loadClosures = async () => {
      const response = await store.dispatch(
        actions.monthlyClosureActions.fetchClosures,
        fetchParams.value
      );
      if (response?.ok && !Array.isArray(response.data)) {
        store.commit("setMonthlyClosures", normalizeList(response));
      }
    };

    const openDetail = async (closure) => {
      selectedClosureId.value = closure.id;
      await store.dispatch(actions.monthlyClosureActions.fetchOne, closure.id);
    };

    const continueClosure = (closure) => {
      router.push({
        path: "/contabilidad/cierre-mensual",
        query: { closureId: closure.id },
      });
    };

    const clearDetail = () => {
      selectedClosureId.value = null;
      selectedClosures.value = [];
      store.commit("setCurrentMonthlyClosure", null);
    };

    const requireOneSelected = () => {
      if (selectedClosures.value.length !== 1) {
        alert("Debe seleccionar un registro!");
        return null;
      }
      return selectedClosures.value[0];
    };

    const onViewSelectedDetail = () => {
      const closure = requireOneSelected();
      if (closure) openDetail(closure);
    };

    const onContinueSelected = () => {
      const closure = requireOneSelected();
      if (!closure) return;
      if (statusKey(closure.status) !== "draft") {
        alert("Solo se puede continuar un cierre en estado borrador.");
        return;
      }
      continueClosure(closure);
    };

    const listingActions = [
      { name: "Ver detalle", action: onViewSelectedDetail, hidden: true },
      { name: "Continuar cierre", action: onContinueSelected, hidden: true },
    ];

    const periodLabel = (closure) => {
      const month = monthOptions.find((item) => Number(item.id) === Number(closure?.month));
      return `${month?.name || "-"} ${closure?.year || ""}`.trim();
    };

    const statusKey = (status) => String(status || "unknown").toLowerCase();
    const statusLabel = (status) => {
      const key = statusKey(status);
      if (key === "draft") return "Borrador";
      if (key === "closed") return "Cerrado";
      return text(status);
    };

    const documentLabel = (document) => {
      const role = getValue(document, ["documentRole", "metadata.documentRole"], "");
      if (role === "building_summary") return "Resumen del edificio";
      const apartment = getValue(document, ["apartmentNumber", "metadata.apartmentNumber"], null);
      if (apartment) return `Liquidacion apartamento ${apartment}`;
      return "Abrir PDF";
    };

    watch(buildingId, () => {
      loadClosures();
      clearDetail();
    });

    onMounted(() => {
      ensureBreadcrumb();
      loadClosures();
    });

    return {
      filters,
      monthOptions,
      statusOptions,
      selectedClosureId,
      selectedClosures,
      isLoading,
      errorMessage,
      filteredClosures,
      tableColumns,
      tableRows,
      listingActions,
      detailClosure,
      detailWarnings,
      detailDocuments,
      detailApartments,
      detailTotals,
      getValue,
      getTotal,
      text,
      money,
      dateText,
      periodLabel,
      statusKey,
      statusLabel,
      documentLabel,
      loadClosures,
      openDetail,
      continueClosure,
      clearDetail,
    };
  },
};

const getTotal = (closure, key, fallbackKey = null) =>
  getValue(closure, [key, `totals.${key}`, fallbackKey, `totals.${fallbackKey}`], 0);
</script>

<style scoped>
.monthly-closures {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  padding: 2rem 1.5rem 4rem;
}

.monthly-closures__header,
.monthly-closures__filters,
.monthly-closures__table-wrap,
.monthly-closures__message {
  align-self: center;
  width: min(1180px, 100%);
}

.monthly-closures__header {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.monthly-closures__listing {
  width: 100%;
}

.monthly-closures__detail {
  align-self: center;
  width: calc(100% - 4rem);
}

.monthly-closures__filters,
.monthly-closures__detail {
  background: #fff;
  border: 1px solid #dbe7e5;
  border-radius: 8px;
  box-shadow: 0 10px 30px rgba(35, 141, 130, 0.08);
  padding: 1.25rem;
}

.monthly-closures__filters {
  align-items: end;
  display: grid;
  gap: 1rem;
  grid-template-columns: repeat(3, minmax(140px, 1fr)) auto;
}

.monthly-closures__field {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}

.monthly-closures__field label {
  color: var(--primary-dark);
  font-family: "klavika";
  font-size: 0.9rem;
}

.monthly-closures__control {
  min-height: 48px;
  width: 100%;
}

.monthly-closures__button {
  background-color: var(--primary) !important;
  border-color: var(--primary) !important;
  box-shadow: none !important;
  min-height: 48px;
}

.monthly-closures__loading {
  align-items: center;
  color: var(--primary-dark);
  display: flex;
  gap: 1rem;
  justify-content: center;
}

.monthly-closures__loading :deep(.p-progress-spinner) {
  height: 2rem;
  width: 2rem;
}

.monthly-closures__message {
  border-radius: 8px;
  padding: 1rem;
}

.monthly-closures__message--error {
  background: #fee2e2;
  color: #991b1b;
}

.monthly-closures__message--warn {
  background: #fff7ed;
  color: #9a3412;
  margin-top: 1rem;
}

.monthly-closures__message ul {
  margin: 0.75rem 0 0;
  padding-left: 1.25rem;
}

.monthly-closures__table-wrap {
  overflow-x: auto;
}

.monthly-closures__detail .monthly-closures__table-wrap {
  width: 100%;
}

.monthly-closures__table {
  background: #fff;
  border-collapse: collapse;
  min-width: 1080px;
  width: 100%;
}

.monthly-closures__table th,
.monthly-closures__table td {
  font-family: "klavika";
  padding: 0.75rem;
  vertical-align: top;
}

.monthly-closures__table td {
  border-bottom: 1px solid #e2e8f0;
  color: gray;
}

.monthly-closures__table th {
  background: var(--primary-light);
  border: 2px solid var(--primary-light);
  color: var(--primary-dark);
  font-weight: 600;
  text-align: center;
}

.monthly-closures__row--selected {
  background: #f0fdfa;
}

.monthly-closures__badge {
  border-radius: 999px;
  display: inline-block;
  font-size: 0.82rem;
  font-weight: 600;
  padding: 0.25rem 0.6rem;
}

.monthly-closures__badge--draft {
  background: #ecfdf5;
  color: #166534;
}

.monthly-closures__badge--closed {
  background: #eef2ff;
  color: #3730a3;
}

.monthly-closures__badge--unknown {
  background: #f1f5f9;
  color: #475569;
}

.monthly-closures__link-button {
  background: transparent;
  border: none;
  color: var(--primary);
  cursor: pointer;
  display: block;
  font-weight: 600;
  margin-bottom: 0.35rem;
  padding: 0;
}

.monthly-closures__empty {
  color: #64748b;
  margin: 1rem 0;
  text-align: center;
}

.monthly-closures__empty--left {
  text-align: left;
}

.monthly-closures__detail-header {
  align-items: flex-start;
  display: flex;
  gap: 1rem;
  justify-content: space-between;
}

.monthly-closures__detail-header h3,
.monthly-closures__detail-header p {
  margin: 0;
}

.monthly-closures__detail-header h3,
.monthly-closures__detail-section h4 {
  color: var(--primary-dark);
}

.monthly-closures__detail-header p {
  color: #64748b;
  margin-top: 0.25rem;
}

.monthly-closures__detail-header button {
  background: transparent;
  border: none;
  color: var(--primary);
  cursor: pointer;
  font-weight: 600;
}

.monthly-closures__cards {
  display: grid;
  gap: 1rem;
  grid-template-columns: repeat(auto-fit, minmax(170px, 1fr));
  margin-top: 1.25rem;
}

.monthly-closures__card {
  border: 1px solid #dbe7e5;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
  min-height: 86px;
  padding: 1rem;
}

.monthly-closures__card span {
  color: #64748b;
  font-size: 0.9rem;
}

.monthly-closures__card strong {
  color: var(--primary-dark);
  font-size: 1.1rem;
}

.monthly-closures__detail-section {
  border-top: 1px solid #e2e8f0;
  margin-top: 1.25rem;
  padding-top: 1.25rem;
}

.monthly-closures__documents {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
}

.monthly-closures__documents a,
.monthly-closures__table a {
  color: var(--primary);
  font-weight: 600;
}

.monthly-closures__table--detail {
  margin-top: 1rem;
  min-width: 100%;
}

@media screen and (max-width: 900px) {
  .monthly-closures__filters {
    grid-template-columns: 1fr;
  }

  .monthly-closures__detail-header {
    flex-direction: column;
  }
}

@media screen and (min-width: 768px) {
  .monthly-closures__detail {
    width: calc(100% - 8rem);
  }
}
</style>
